import React, { useState, useEffect } from 'react';
import SearchHeader from './components/SearchHeader';
import SongCard from './components/SongCard';
import SongDetailModal from './components/SongDetailModal';
import PasswordGate from './components/PasswordGate';
import HealthCheck from './components/HealthCheck';
import { useSearch } from './hooks/useSearch';

function App() {
  const [query, setQuery] = useState('');
  const [collection, setCollection] = useState(null);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(!!localStorage.getItem('rehearsal_password') || !!localStorage.getItem('choir_token'));
  const [tier] = useState(localStorage.getItem('choir_token') ? 'Member' : 'Guest');

  const { results, loading, error } = useSearch(query, null, collection);

  // Handle 401 errors globally
  useEffect(() => {
    if (error && error.includes('Access denied')) {
      localStorage.removeItem('choir_token');
      localStorage.removeItem('rehearsal_password');
      setIsAuthorized(false);
    }
  }, [error]);

  if (!isAuthorized) {
    return <PasswordGate onAuthorized={() => setIsAuthorized(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 selection:bg-blue-100 selection:text-blue-900 flex flex-col">
      {/* Search Header */}
      <SearchHeader 
        query={query} 
        setQuery={setQuery} 
        collection={collection} 
        setCollection={setCollection} 
      />

      <main className="flex-1 max-w-5xl w-full mx-auto p-4 sm:p-6 pb-24">
        
        {/* Tier Status & Info */}
        <div className="flex justify-between items-center mb-6 px-1">
          <button 
            onClick={() => {
              if (tier === 'Guest') {
                if (window.confirm('Would you like to log in as an Administrator?')) {
                  window.location.href = 'https://admin.bchoir.app';
                }
              }
            }}
            className={`flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-100 transition text-left ${tier === 'Guest' ? 'hover:bg-gray-50 active:scale-95 cursor-pointer' : ''}`}
          >
            <span className={`w-2.5 h-2.5 rounded-full ${tier === 'Member' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 'bg-orange-400 animate-pulse'}`}></span>
            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">
              {tier} Access
            </p>
          </button>
          {/* TODO: Implement Google OAuth integration for Member Tier elevation */}
          {/* {tier === 'Guest' && (
             <button 
              className="text-[11px] font-bold text-blue-600 hover:text-blue-800 transition uppercase tracking-wider hover:underline"
              onClick={() => alert('Redirect to Google Login...')}
            >
              Sign In
            </button>
          )} */}
        </div>

        {/* Results Info */}
        {query.length >= 2 && !loading && (
          <div className="mb-6 animate-fade-in">
             <p className="text-sm font-semibold text-gray-500 ml-1">
              Found <span className="text-gray-900">{results.length}</span> results for "<span className="text-gray-900">{query}</span>"
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-32 space-y-6 animate-fade-in">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-100 rounded-full animate-pulse"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
            </div>
            <p className="text-gray-500 font-medium tracking-wide">Searching the archive...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-100 p-6 rounded-2xl text-center max-w-md mx-auto my-10 animate-fade-in">
            <div className="w-12 h-12 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
               <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <h3 className="text-red-900 font-bold mb-1">Search Failed</h3>
            <p className="text-red-600 text-sm">{error}. Please try again later.</p>
          </div>
        )}

        {/* Empty State */}
        {query.length >= 2 && results.length === 0 && !loading && (
          <div className="text-center py-32 animate-fade-in">
            <div className="bg-gray-100 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 text-gray-400">
               <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
               </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No matches found</h3>
            <p className="text-gray-500">We couldn't find any songs matching "{query}".</p>
            <button 
              onClick={() => setQuery('')}
              className="mt-6 px-6 py-2 bg-white border border-gray-300 rounded-full text-sm font-semibold text-gray-700 hover:bg-gray-50 transition shadow-sm"
            >
              Clear Search
            </button>
          </div>
        )}

        {/* Welcome / Hero State */}
        {query.length < 2 && !loading && (
          <div className="py-12 sm:py-20 text-center space-y-8 animate-fade-in">
            <div className="relative inline-block">
               <div className="absolute inset-0 bg-blue-200 blur-3xl opacity-30 rounded-full animate-pulse"></div>
               <div className="bg-gradient-to-tr from-blue-600 to-blue-400 w-24 h-24 rounded-3xl flex items-center justify-center mx-auto text-white shadow-xl rotate-3 hover:rotate-6 transition-transform duration-500 relative z-10">
                 <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                 </svg>
               </div>
            </div>
            
            <div className="max-w-xl mx-auto space-y-4">
              <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
                Search the Music Library.
              </h1>
              <p className="text-lg text-gray-500 leading-relaxed">
                Welcome to the digital music library. <br className="hidden sm:block" />
                Start by typing a song title.
              </p>
            </div>
          </div>
        )}

        {/* Results Grid */}
        <div className="grid grid-cols-1 gap-4">
          {results.map((song) => (
            <SongCard 
              key={song.song_slug} 
              song={song} 
              onClick={setSelectedSlug} 
            />
          ))}
        </div>
      </main>

      {/* Modals */}
      <SongDetailModal 
        slug={selectedSlug} 
        onClose={() => setSelectedSlug(null)} 
      />

      <HealthCheck />

      {/* Footer */}
      <footer className="mt-auto border-t border-gray-200 bg-white py-8">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
            bChoir Portal
          </p>
          <p className="text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Church Organization. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
// Trigger production build
